import type { WindowState } from './WindowManager';

// A Desktop-skin layout as a link (`?desk=`): which windows, where, how big. Paths are app routes
// only — a link cannot open anything outside NovaPSA (no scheme, no host, no protocol-relative
// `//`). At most 12 windows; anything malformed is ignored rather than half-applied.

export interface SharedWindow { path: string; title: string; x: number; y: number; w: number; h: number; max?: boolean }

const MAX_WINDOWS = 12;
const PATH_RE = /^\/(?!\/)[^\s\\]{0,500}$/;

const b64url = (s: string) => {
  let bin = '';
  for (const b of new TextEncoder().encode(s)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const unb64url = (s: string) => new TextDecoder().decode(Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)));

export function encodeLayout(windows: Pick<WindowState, 'path' | 'title' | 'x' | 'y' | 'w' | 'h' | 'maximized' | 'minimized' | 'z'>[]): string {
  const list: SharedWindow[] = [...windows]
    .filter((w) => !w.minimized)
    .sort((a, b) => a.z - b.z)
    .slice(-MAX_WINDOWS)
    .map((w) => ({ path: w.path, title: w.title.slice(0, 120), x: Math.round(w.x), y: Math.round(w.y), w: Math.round(w.w), h: Math.round(w.h), ...(w.maximized ? { max: true } : {}) }));
  return b64url(JSON.stringify(list));
}

const num = (v: unknown, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : null);

export function decodeLayout(raw: string | null | undefined): SharedWindow[] | null {
  if (!raw || raw.length > 8000) return null;
  let v: unknown;
  try {
    v = JSON.parse(unb64url(raw));
  } catch {
    return null;
  }
  if (!Array.isArray(v) || v.length === 0 || v.length > MAX_WINDOWS) return null;
  const out: SharedWindow[] = [];
  for (const it of v) {
    const o = it as Record<string, unknown>;
    if (typeof o['path'] !== 'string' || !PATH_RE.test(o['path'])) return null;
    const x = num(o['x'], 0, 10_000), y = num(o['y'], 0, 10_000), w = num(o['w'], 200, 10_000), h = num(o['h'], 150, 10_000);
    if (x === null || y === null || w === null || h === null) return null;
    out.push({ path: o['path'], title: typeof o['title'] === 'string' ? o['title'].slice(0, 120) : o['path'], x, y, w, h, ...(o['max'] === true ? { max: true } : {}) });
  }
  return out;
}
